import { cn, getColorway } from 'utils/styles';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { CardsStackedItemBackwardsCompatibleProps } from './types';
import type { MediaAsset, DocumentWithEmbeddedEntries } from 'libs/contentful/types';
import { Button } from '@1debit/chimekit';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import RichTextList from 'components/shared/RichTextList/RichTextList';
import BadgeOverlay from 'components/shared/BadgeOverlay/BadgeOverlay';
import { BackgroundColor, BadgeOverlayAlignment, CopyAlignment, BulletStyle, BulletListStyle } from 'types/constants';
import Media from '../shared/Media/Media';
import BackwardsCompatibleText from 'components/shared/BackwardsCompatibleText/BackwardsCompatibleText';
import { isValidAnimation } from '@/components/shared/Media/mediaUtils';

import { Block, Inline, TopLevelBlock } from '@contentful/rich-text-types';
import CTA from '../shared/CTA/CTA';

const hasUnorderedList = (doc: DocumentWithEmbeddedEntries | undefined): boolean => {
  if (!doc?.json?.content) return false;

  const checkNodes = (nodes: (TopLevelBlock | Block | Inline)[]): boolean => {
    for (const node of nodes) {
      if (node.nodeType === 'unordered-list') return true;
      if (node.content && Array.isArray(node.content)) {
        if (checkNodes(node.content! as (TopLevelBlock | Block | Inline)[])) return true;
      }
    }
    return false;
  };

  return checkNodes(doc.json.content);
};

interface Props
  extends CardsStackedItemBackwardsCompatibleProps, Pick<React.ComponentProps<typeof Button>, 'iconProps'> {
  badgeOverlayAlignment?: BadgeOverlayAlignment;
  copyAlignment?: CopyAlignment;
  bulletStyle?: BulletStyle;
  iconAsset?: MediaAsset;
  isCheckmarkList?: React.ReactNode;
}

function CardsStackedItemImageAboveCopy({
  sys,
  sectionName,
  desktopMedia,
  mobileMedia,
  eyebrow,
  eyebrowRte,
  headline,
  shortDescription,
  cta,
  color,
  topRightBadge,
  badgeOverlayAlignment = BadgeOverlayAlignment.Right,
  copyAlignment = CopyAlignment.Left,
  bulletStyle,
  iconAsset,
  desktopAnimation,
  mobileAnimation,
  desktopAspectRatio,
  mobileAspectRatio,
}: Props) {
  const isGradient = BackgroundColor.DarkGreenGradient === getColorway(color);
  const hasDesktopAnimation = isValidAnimation(desktopAnimation);
  const hasMobileAnimation = isValidAnimation(mobileAnimation);

  const cardWrapperClasses = cn(
    'scroll-mt-[var(--height-nav)] relative  w-full min-h-[514px]',
    'sm:h-[600px] md:min-h-[800px]',
    'group-data-[num-items=4]:md:basis-1/2-gap-6 rounded-[16px] md:rounded-[24px] active:rounded-[16px] md:active:rounded-[24px] active:bg-shade-transparent active:scale-100 overflow-hidden',
    'flex flex-col justify-end gap-6 md:gap-12 md:basis-1/3-gap-6 md:grow-1',
    {
      'bg-[image:var(--dark-green-gradient-mobile)] active:bg-[image:var(--dark-green-gradient-mobile)]': isGradient,
      'sm:bg-[image:var(--dark-green-gradient-desktop)] sm:active:bg-[image:var(--dark-green-gradient-desktop)]':
        isGradient,
    },
    getColorway(color),
  );

  const isOutlineButton = cta?.variant?.toLowerCase() === 'outline';
  const isCentered = copyAlignment === CopyAlignment.Centered;
  const isCheckmarkList = bulletStyle === BulletStyle.CheckBoxes && hasUnorderedList(shortDescription);

  return (
    <div
      {...(sectionName != null && { id: encodeURIComponent(sectionName) })}
      className={cardWrapperClasses}
      data-testid="cardsStackedItemImageAboveCopy">
      {topRightBadge && (
        <BadgeOverlay
          description={topRightBadge.image.description}
          sys={topRightBadge.sys}
          url={topRightBadge.image.url}
          fileName={topRightBadge.image.fileName}
          badgeOverlayAlignment={badgeOverlayAlignment}
        />
      )}
      <div className="h-full flex flex-col gap-6 justify-between">
        <div className="rounded-t-md  flex-1 flex-grow overflow-hidden">
          {/* Desktop: Show animation if available, otherwise static media */}
          {hasDesktopAnimation ? (
            <div className="hidden sm:flex h-full w-full items-center justify-center overflow-hidden">
              <Media desktopAnimation={desktopAnimation} className="max-h-full rounded-none object-contain" />
            </div>
          ) : (
            (desktopMedia || mobileMedia) && (
              <div className="hidden sm:block h-full w-full">
                <Media
                  media={{ ...(desktopMedia || mobileMedia)!, fill: false }}
                  aspectRatio={desktopAspectRatio}
                  sizes="(max-width: 1024px) 100vw, (max-width: 1440px) 50vw, 650px"
                  className="h-full w-full rounded-none object-contain"
                />
              </div>
            )
          )}
          {/* Mobile: Show animation if available, otherwise static media */}
          {hasMobileAnimation ? (
            <div className="block sm:hidden h-full w-full">
              <Media mobileAnimation={mobileAnimation} className="h-full w-full rounded-none" />
            </div>
          ) : (
            (mobileMedia || desktopMedia) && (
              <div className="block sm:hidden h-full w-full">
                <Media
                  media={{ ...(mobileMedia || desktopMedia)!, fill: false }}
                  aspectRatio={mobileAspectRatio}
                  sizes="100vw"
                  className="h-full w-full rounded-none object-contain"
                />
              </div>
            )
          )}
        </div>

        <div className="w-full flex flex-col relative z-10 px-6 md:px-12 pt-6 md:pt-6 pb-10 md:pb-14">
          {eyebrow || eyebrowRte ? (
            <span
              className={cn(
                'font-sans font-semibold text-eyebrow',
                'text-[var(--color-fern)] text-md',
                'pb-0',
                isCentered ? 'text-center' : '',
              )}
              {...liveUpdateAttrs(sys?.id, 'eyebrow')}
              data-testid="eyebrow">
              <BackwardsCompatibleText text={eyebrow} richText={eyebrowRte} />
            </span>
          ) : null}
          {iconAsset && (
            <div
              {...liveUpdateAttrs(sys?.id, 'iconAsset')}
              data-testid="iconAsset"
              className={cn('flex justify-center pb-4', {
                'justify-center': copyAlignment === CopyAlignment.Centered,
                'justify-start': copyAlignment === CopyAlignment.Left,
              })}>
              <Media
                media={iconAsset}
                className={cn('max-h-10 object-contain rounded-none', {
                  'object-center': copyAlignment === CopyAlignment.Centered,
                  'object-left': copyAlignment === CopyAlignment.Left,
                })}
              />
            </div>
          )}
          {headline && (
            <div
              className={cn(
                'text-heading-md font-sans',
                'sm:text-heading-lg sm:font-[570]',
                isCentered ? 'text-center' : '',
                iconAsset ? 'hidden' : '',
              )}
              {...liveUpdateAttrs(sys?.id, 'headline')}
              data-testid="headline">
              <RichTextHeadline headline={headline} level="h2" />
            </div>
          )}
          {shortDescription && (
            <div
              className={cn(
                'leading-lg pt-4 sm:pt-4 md:pt-4 ml:pt-6 font-serif [&>ul>li]:items-baseline',
                // List-specific styles when using RichTextList with checkmark bullets
                ...(isCheckmarkList
                  ? [
                      'pb-0 sm:pb-0 md:pb-0 ml:pb-0 lg:pb-0',
                      '[&>ul]:flex [&>ul]:flex-col [&>ul]:text-md',
                      '[&>ul>li]:text-lg [&>ul>li]:!align-center [&>ul>li>span]:lg:pt-1.5 [&>ul>li]:ml:text-md [&>ul>li]:my-1 [&>ul>li]:sm:my-0.5',
                    ]
                  : ['text-lg leading-md']),
                isCentered ? 'text-center mx-auto' : '',

                {
                  'text-center mx-auto [&>ul]:items-center': copyAlignment === CopyAlignment.Centered,
                  'text-left': copyAlignment === CopyAlignment.Left,
                },
              )}
              {...liveUpdateAttrs(sys?.id, 'shortDescription')}
              data-testid="shortDescription">
              {isCheckmarkList ? (
                <RichTextList
                  item={shortDescription}
                  arrowStyles="text-[var(--color-fern)]"
                  bulletListStyle={BulletListStyle.Checkmark}
                  checkmarkSize={16}
                />
              ) : (
                <RichTextCopy copy={shortDescription} ySpacing="none" arrowStyles="text-[var(--color-fern)]" />
              )}
            </div>
          )}
          {cta ? (
            <div
              className={cn('mt-6 sm:mt-8 md:mt-12', 'w-full', 'flex items-center gap-2', {
                'justify-center': copyAlignment === CopyAlignment.Centered,
                'justify-start': copyAlignment === CopyAlignment.Left,
              })}>
              <CTA
                cta={cta}
                variant="outline"
                className={cn(
                  'place-self-center link-button-outline border-[1.5px] border-(--btn-outline)',
                  'hover:border-(--btn-outline) font-sans font-semibold flex items-center justify-center gap-2 sm:max-w-max',
                  {
                    'hover:border-(--btn-outline-hover) link-button-outline border-[1.5px]': isOutlineButton,
                    'py-[6.5px] px-14 rounded-md text-current hover:text-current leading-tight': isOutlineButton,
                  },
                )}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default CardsStackedItemImageAboveCopy;
